import React, { useState, useEffect, useCallback } from 'react';
import { Rss, Heart, MessageSquare, Trash2, Send, Image as ImageIcon, X } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { feed as api, type Post, type PostComment, type ApiError } from '../../services/api';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};

const PAGE_SIZE = 20;

const initials = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

const fmt = (iso: string) =>
  new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const Avatar: React.FC<{ name: string }> = ({ name }) => (
  <div style={{
    width: 36, height: 36, borderRadius: '50%', background: 'var(--nx-blue)15',
    color: 'var(--nx-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 700, flexShrink: 0,
  }}>
    {initials(name)}
  </div>
);

const CommentThread: React.FC<{
  sid: string;
  postId: string;
  canModerate: boolean;
  onCountChange: (delta: number) => void;
}> = ({ sid, postId, canModerate, onCountChange }) => {
  const [comments, setComments] = useState<PostComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    api.listComments(sid, postId).then(setComments).catch(() => {}).finally(() => setLoading(false));
  }, [sid, postId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setSending(true);
    try {
      const created = await api.createComment(sid, postId, draft.trim());
      setComments(p => [...p, created]);
      setDraft('');
      onCountChange(1);
    } catch { /* ignore */ } finally { setSending(false); }
  };

  const remove = async (commentId: string) => {
    setDeletingId(commentId);
    try {
      await api.deleteComment(sid, postId, commentId);
      setComments(p => p.filter(c => c.id !== commentId));
      onCountChange(-1);
    } catch { /* ignore */ } finally { setDeletingId(null); }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--nx-border)' }}>
      {loading ? (
        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Loading comments…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10 }}>
          {comments.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <Avatar name={c.authorName} />
              <div style={{ flex: 1, background: 'var(--nx-surface-3)', borderRadius: 10, padding: '6px 10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-primary)' }}>{c.authorName}</span>
                  {(c.mine || canModerate) && (
                    <button
                      onClick={() => remove(c.id)}
                      disabled={deletingId === c.id}
                      title="Delete comment"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: 2, display: 'flex' }}
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--nx-text-secondary)', lineHeight: 1.5 }}>{c.content}</div>
              </div>
            </div>
          ))}
          {comments.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No comments yet.</div>
          )}
        </div>
      )}
      <form onSubmit={submit} style={{ display: 'flex', gap: 8 }}>
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Write a comment…"
          style={{ ...inp, flex: 1 }}
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          className="nx-btn nx-btn-primary"
          style={{ padding: '8px 12px', display: 'flex', alignItems: 'center' }}
        >
          <Send size={14} />
        </button>
      </form>
    </div>
  );
};

const CommunityFeedPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const canModerate = user?.role === 'SOCIETY_ADMIN' || user?.role === 'STAFF' || user?.role === 'SUPER_ADMIN';

  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [draft, setDraft] = useState('');
  const [photoBase64, setPhotoBase64] = useState<string | undefined>(undefined);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState('');

  const [likingId, setLikingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Post | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [openComments, setOpenComments] = useState<Set<string>>(new Set());

  const loadPage = useCallback((pageNum: number) => {
    if (!societyId) return;
    const setBusy = pageNum === 0 ? setLoading : setLoadingMore;
    setBusy(true);
    api.listPosts(societyId, pageNum, PAGE_SIZE)
      .then(res => {
        setPosts(prev => pageNum === 0 ? res.content : [...prev, ...res.content]);
        setHasMore(pageNum + 1 < res.totalPages);
        setPage(pageNum);
      })
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [societyId]);

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    loadPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societyId]);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoBase64(reader.result as string);
    reader.readAsDataURL(file);
  };

  const submitPost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !draft.trim()) return;
    setPosting(true); setPostError('');
    try {
      const created = await api.createPost(societyId, { content: draft.trim(), photoBase64 });
      setPosts(p => [created, ...p]);
      setDraft(''); setPhotoBase64(undefined);
    } catch (err) {
      setPostError((err as ApiError).message || 'Failed to post');
    } finally { setPosting(false); }
  };

  const toggleLike = async (post: Post) => {
    if (!societyId) return;
    setLikingId(post.id);
    try {
      const updated = await api.toggleLike(societyId, post.id);
      setPosts(p => p.map(x => x.id === post.id ? updated : x));
    } catch { /* ignore */ } finally { setLikingId(null); }
  };

  const toggleComments = (id: string) => {
    setOpenComments(p => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const adjustCommentCount = (postId: string, delta: number) => {
    setPosts(p => p.map(x => x.id === postId ? { ...x, commentCount: Math.max(0, x.commentCount + delta) } : x));
  };

  const confirmDelete = async () => {
    if (!societyId || !deleteTarget) return;
    setDeleting(true);
    try {
      await api.deletePost(societyId, deleteTarget.id);
      setPosts(p => p.filter(x => x.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch { /* ignore */ } finally { setDeleting(false); }
  };

  return (
    <PageLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
          Community Feed
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>{posts.length} posts loaded</p>
      </div>

      {/* Composer */}
      <div className="nx-card" style={{ padding: 18, marginBottom: 20 }}>
        <form onSubmit={submitPost}>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Share something with your community…"
            style={{ ...inp, minHeight: 70, resize: 'vertical', marginBottom: 10 }}
          />
          {photoBase64 && (
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: 10 }}>
              <img src={photoBase64} alt="attachment preview" style={{ maxHeight: 140, borderRadius: 10, display: 'block' }} />
              <button
                type="button"
                onClick={() => setPhotoBase64(undefined)}
                style={{ position: 'absolute', top: 6, right: 6, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={12} />
              </button>
            </div>
          )}
          {postError && (
            <div style={{ fontSize: 12, color: '#EF4444', marginBottom: 10 }}>{postError}</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--nx-text-muted)', cursor: 'pointer' }}>
              <ImageIcon size={15} />
              Add photo
              <input type="file" accept="image/*" onChange={handlePhotoSelect} style={{ display: 'none' }} />
            </label>
            <button
              type="submit"
              disabled={posting || !draft.trim()}
              className="nx-btn nx-btn-primary"
              style={{ gap: 6 }}
            >
              {posting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </form>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading…</div>
      ) : posts.length === 0 ? (
        <div className="nx-card" style={{ padding: 60, textAlign: 'center' }}>
          <Rss size={32} style={{ color: 'var(--nx-text-muted)', display: 'block', margin: '0 auto 12px' }} />
          <div style={{ fontSize: 14, color: 'var(--nx-text-muted)' }}>No posts yet — be the first to share something.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {posts.map(post => (
            <div key={post.id} className="nx-card" style={{ padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <Avatar name={post.authorName} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text-primary)' }}>{post.authorName}</div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{fmt(post.createdAt)}</div>
                  </div>
                </div>
                {(post.mine || canModerate) && (
                  <button
                    onClick={() => setDeleteTarget(post)}
                    title="Delete post"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: 4, display: 'flex' }}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              <p style={{ fontSize: 13, color: 'var(--nx-text-secondary)', lineHeight: 1.6, marginBottom: post.photoBase64 ? 10 : 12, whiteSpace: 'pre-wrap' }}>
                {post.content}
              </p>
              {post.photoBase64 && (
                <img src={post.photoBase64} alt="post attachment" style={{ maxWidth: '100%', borderRadius: 10, marginBottom: 12, display: 'block' }} />
              )}
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <button
                  onClick={() => toggleLike(post)}
                  disabled={likingId === post.id}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: post.likedByMe ? '#EF4444' : 'var(--nx-text-muted)' }}
                >
                  <Heart size={14} fill={post.likedByMe ? '#EF4444' : 'none'} />
                  {post.likeCount}
                </button>
                <button
                  onClick={() => toggleComments(post.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--nx-text-muted)' }}
                >
                  <MessageSquare size={14} />
                  {post.commentCount}
                </button>
              </div>
              {openComments.has(post.id) && (
                <CommentThread
                  sid={societyId as string}
                  postId={post.id}
                  canModerate={canModerate}
                  onCountChange={delta => adjustCommentCount(post.id, delta)}
                />
              )}
            </div>
          ))}
          {hasMore && (
            <button
              onClick={() => loadPage(page + 1)}
              disabled={loadingMore}
              className="nx-btn"
              style={{ alignSelf: 'center', fontSize: 12 }}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        variant="danger"
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
        confirmLabel="Delete Post"
        isLoading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </PageLayout>
  );
};

export default CommunityFeedPage;
